import { createContext, useContext, useEffect, useState } from "react";
import type { ReactNode } from "react";
import { useLocation } from "react-router-dom";
import {
  initOAuth,
  getUserInfo,
  getCachedUserInfo,
  clearTokenAndRedirectToLogin,
  redirectToLogin,
  getOAuthCodeFromUrl,
} from "@/lib/oauth";
import { track, setTrackUserId } from "@/lib/track";
import type { UserInfo } from "@/types";
import SsoFailedPage from "@/pages/SsoFailedPage";

interface AuthContextType {
  isAuthenticated: boolean;
  isLoading: boolean;
  userInfo: UserInfo | null;
  /** SSO 回调换 token 失败，需用户手动重新登录 */
  ssoCallbackFailed: boolean;
  /** 跳转门户登录 */
  login: () => void;
  /** 清除登录态并跳转登录 */
  logout: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function useAuth(): AuthContextType {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const { pathname } = useLocation();
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [userInfo, setUserInfo] = useState<UserInfo | null>(null);
  const [ssoCallbackFailed, setSsoCallbackFailed] = useState(false);

  // 初始化认证 + 拉取用户信息：仅在挂载时执行一次，路由切换不重复请求
  useEffect(() => {
    // initOAuth 内部会清除 URL 中的 oauth_code，需在调用前检测是否为 SSO 回调
    const isSsoCallback = !!getOAuthCodeFromUrl();
    const initialize = async () => {
      try {
        const success = await initOAuth();
        if (success) {
          setIsAuthenticated(true);
          // 获取用户信息，suppress401 避免用户信息接口 401 触发跳转
          const info = await getUserInfo(true);
          setUserInfo(info);
          // 注入 userId 到埋点模块
          if (info?.id) {
            setTrackUserId(info.id);
          }
          // SSO 回调登录成功 -> 埋点（initOAuth 会清除 URL 中的 oauth_code，需先检测）
          if (isSsoCallback && info?.id) {
            track("login", "page", { userId: info.id });
          }
        } else if (isSsoCallback) {
          // SSO 回调但换 token 失败：展示提示页，避免自动跳转门户造成死循环
          setSsoCallbackFailed(true);
        }
      } catch {
        // 初始化异常：若是 SSO 回调则同样标记失败，避免死循环
        if (isSsoCallback) {
          setSsoCallbackFailed(true);
        }
      } finally {
        setIsLoading(false);
      }
    };
    initialize();
  }, []);

  // 路由守卫：仅本地状态判断，未登录访问受保护页面才跳转，不请求接口
  useEffect(() => {
    if (isLoading) return;
    if (isAuthenticated) return;
    if (ssoCallbackFailed) return; // SSO 回调失败，展示提示页，不自动跳转
    if (pathname === "/login") {
      // 登录页放行，展示缓存的用户信息（如有）
      setUserInfo(getCachedUserInfo());
      return;
    }
    clearTokenAndRedirectToLogin();
  }, [pathname, isAuthenticated, isLoading, ssoCallbackFailed]);

  const login = () => redirectToLogin();
  const logout = () => {
    track("logout", "page", { userId: userInfo?.id });
    clearTokenAndRedirectToLogin();
  };

  if (ssoCallbackFailed) {
    return (
      <AuthContext.Provider
        value={{ isAuthenticated, isLoading, userInfo, ssoCallbackFailed, login, logout }}
      >
        <SsoFailedPage />
      </AuthContext.Provider>
    );
  }

  return (
    <AuthContext.Provider
      value={{ isAuthenticated, isLoading, userInfo, ssoCallbackFailed, login, logout }}
    >
      {children}
    </AuthContext.Provider>
  );
}
